import React, { useState, useCallback, useEffect } from 'react'
import propTypes from 'prop-types'
import { Level, Container, Box, Collapse, Dropdown } from '@deseretbook/react-ui'
import { Slider } from '../../../../dbWebUI'
import * as AudiobookHelpers from '../../../../utils/base'
import ControlView from './ControlView'
import VolumeView from './VolumeView'
import PlaybackRateView from './PlaybackRateView'
import { useDropdown } from '@deseretbook/react-utils'
import { remainingDuration } from '../../../../utils/remainingDuration'

export const AUDIOBOOK_CONTROL_VIEWS = {
  control: 'control',
  volume: 'volume',
  playbackRate: 'playbackRate',
}

/**
 * Audiobook controls
 */
const AudiobookControls = (props) => {
  const {
    progress: propsProgress,
    duration,
    isLoading,
    seekTo,
    onChangeRate,
    volume,
    onChangeVolume,
    rate,
    chapters,
    currentChapter,
    onSelectChapter,
    totalTime,
  } = props
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState(propsProgress)
  const [currentView, setCurrentView] = useState(AUDIOBOOK_CONTROL_VIEWS.control)

  const dropdown = useDropdown()

  // Sync state with props.progress unless dragging
  useEffect(() => {
    if (!dragging) {
      setProgress(propsProgress)
    }
  }, [propsProgress, dragging])

  const handleBeforeChange = useCallback(() => {
    setDragging(true)
  }, [])

  const handleAfterChange = useCallback(
    (progress) => {
      setDragging(false)
      seekTo(progress)
    },
    [seekTo],
  )

  const handleChangeRate = useCallback(
    (rate) => {
      onChangeRate(rate)
    },
    [onChangeRate],
  )

  const handleChangeView = useCallback((view) => {
    setCurrentView(view)
  }, [])

  const currentTime = AudiobookHelpers.formatDuration(progress || 0)
  const remainingTime = AudiobookHelpers.formatDuration(duration - progress || 0)

  const timeRemaining = remainingDuration(totalTime, progress, chapters, currentChapter)

  const disabled = {
    disabled: duration === 0 || isLoading,
  }

  return (
    <Box style={{ position: 'relative', maxWidth: '356px' }}>
      <Slider
        className="audiobook-ui-slider"
        value={duration === 0 ? 0 : progress}
        min={0}
        max={duration}
        step={1}
        onBeforeChange={handleBeforeChange}
        onChange={(val) => setProgress(val)}
        onAfterChange={handleAfterChange}
      />
      <Container>
        <Level isMobile className="time-level">
          <Level.Left>
            <Box justifyContent="center" flex={1}>
              {currentTime}
            </Box>
          </Level.Left>
          <Level.Right>
            <Box justifyContent="center" flex={1}>
              {remainingTime}
            </Box>
          </Level.Right>
        </Level>
        <Box minHeight={4.5} isColumn justifyContent="center" alignItems="center">
          <ControlView
            disabled={disabled}
            onChangeView={handleChangeView}
            triggerProps={dropdown.triggerProps}
            chapters={chapters}
            currentChapter={currentChapter}
            onSelectChapter={onSelectChapter}
            timeRemaining={timeRemaining}
            {...props}
          />
        </Box>
        <Box minHeight={4.5} isColumn justifyContent="center">
          <Collapse isCollapsed={!(currentView === AUDIOBOOK_CONTROL_VIEWS.volume)}>
            <VolumeView
              volume={volume}
              onChangeVolume={onChangeVolume}
              onChangeView={handleChangeView}
            />
          </Collapse>
          <Dropdown {...dropdown.dropdownProps} id="playbackRateDropdown">
            <PlaybackRateView
              rate={rate}
              onChangeRate={handleChangeRate}
              onChangeView={handleChangeView}
            />
          </Dropdown>
        </Box>
      </Container>
    </Box>
  )
}

AudiobookControls.defaultProps = {
  onChangeRate: () => {},
  previous: () => {},
  play: () => {},
  pause: () => {},
  next: () => {},
  seekTo: () => {},
  onChangeVolume: () => {},
  onPreviousTrack: () => {},
  onNextTrack: () => {},
  isLoading: false,
  isPlaying: false,
  duration: 0.0,
  style: {},
  progress: 0.0,
  volume: 0.15,
  rate: 1,
  chapters: [],
  currentChapter: {},
  onSelectChapter: () => {},
  totalTime: 0,
}

AudiobookControls.propTypes = {
  /** duration: the total duration of the track */
  duration: propTypes.number,
  /** isPlaying: the play state of the track, true is playing, false is paused */
  isPlaying: propTypes.bool,
  /** isLoading: is the track currently in a loading state? */
  isLoading: propTypes.bool,
  /** next: handler to skip to the next track */
  next: propTypes.func,
  /** onChangeVolume: handler for when the volume is changed */
  onChangeVolume: propTypes.func,
  /** onPreviousTrack: move to the previous track */
  onPreviousTrack: propTypes.func,
  /** onNextTrack: move to the next track */
  onNextTrack: propTypes.func,
  /** play: handler to toggle the play status of the current track */
  play: propTypes.func,
  /** previous: handler to seek forward */
  previous: propTypes.func,
  /** progress: total progress in percentage of the current track */
  progress: propTypes.number,
  /** seekTo: handler to seek to a specific point in the track */
  seekTo: propTypes.func,
  /** volume: volume level, number between 0 and 1 */
  volume: propTypes.number,
  /** style: style for the main container */
  style: propTypes.shape({}),
  /** onRateChange: the handler called when the playback rate changes */
  onChangeRate: propTypes.func,
  /** rate: the playback rate of the track */
  rate: propTypes.number,
  /** pause: pause the audio */
  pause: propTypes.func,
  chapters: propTypes.arrayOf(propTypes.shape({})),
  currentChapter: propTypes.shape({}),
  onSelectChapter: propTypes.func,
  totalTime: propTypes.number,
}

export default AudiobookControls
